import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
    Analytics,
    AnalyticsChartType,
    AnalyticsFilter,
    AnalyticsViewMode,
    AppData,
    Dict,
    FieldType,
    QueryType,
    RootState,
    UserRole,
} from 'app/types'
import { useAppSelector, useAppDispatch, useLocalStorage } from 'app/hooks'
import { useAnalyticsData } from 'app/queryHooks'
import {
    applyQuery,
} from 'actions/appActions'
import {
    Spinner,
    Button,
    Icon,
    IconButton,
    DropdownMenu,
    FilterOptions,
    FilterList,
    Input,
    Message,
} from 'components'
import { AnalyticsChartView, AnalyticsChartPointSelection } from 'views/AdminPage/Analytics'
import { setQueryHelper } from 'helpers/query'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { downloadCSV } from 'helpers/csv'
import { ReactMarkdown } from 'react-markdown/lib/react-markdown'
import { analyticsFieldValue } from 'app/transformers'
import { analyticsIcons, icons } from 'app/constants'
import {
    buildAnalyticsAIRequest,
    buildAnalyticsRequest,
    fetchAnalyticsAIRequest,
    fetchAnalyticsRequest,
    MONTHLY_AGGREGATIONS,
} from 'services/analyticsQueryService'
import { AnalyticsWizard } from './AnalyticsWizard'
import { wizardTemplates } from './wizardTemplates'
import { TableOptions } from '../TableOptions'
import { AnalyticsAIContextEditor } from './AnalyticsAIContextEditor'
import { AnalyticsDrilldownPanel } from './AnalyticsDrilldownPanel'

type AnalyticsDefinitionField = Dict & {
    key: string
    map?: string | string[]
    childKey?: string
    hidden?: boolean
    expand?: boolean
}

type AnalyticsDefinition = Dict & {
    fields: AnalyticsDefinitionField[]
    category?: Analytics
    chartType?: AnalyticsChartType
    label?: string
    order?: [string | null, boolean]
}

type AnalyticsSearchFilter = {
    key: string
    label?: string
    value?: unknown
}

interface AnalyticsTableProps {
    tile: Dict,
    app?: AppData
    definitions?: Record<string, AnalyticsDefinition>
    additionalMaps?: Dict
    filters?: string[]
    optionalFilters?: string[]
    searchFilters?: AnalyticsSearchFilter[]
    initialConfig?: Dict | string
    wizardTemplate?: string
    skipWizard?: boolean
    defaultViewMode?: AnalyticsViewMode | string
    readOnly?: boolean
    compactMode?: boolean
    allTime?: boolean
    dashboardDateFilter?: never
    isDashboardTile?: boolean
    marginModifier?: number
    onChange?: (config: Dict) => void
    onTileOperation?: (operation: string, payload: Dict | null) => void
    onEdit?: () => void
    onCancel?: () => void
    onCreate?: (config: Dict) => void
    onAddToDashboard?: (dashboardId: string, config: Dict) => void
    onWizardCancel?: () => void
    order?: [string | null, boolean]
    name?: string
    type?: Analytics
    filterable?: boolean
    onClick?: (value: Dict) => void
    breadcrumbs?: React.ReactNode,
    dashboards?: Dict[]
    topbarPortalTarget?: HTMLElement | null
}

export function AnalyticsTable(props: AnalyticsTableProps) {
    const {
        tile,
        app,
        filterable,
        filters,
        searchFilters,
        onClick,
        additionalMaps,
        wizardTemplate,
        skipWizard,
        defaultViewMode,
        compactMode,
        breadcrumbs,
        onAddToDashboard,
        dashboards,
    } = {
        filterable: true,
        filters: [],
        optionalFilters: [],
        searchFilters: [],
        skipWizard: false,
        defaultViewMode: 'chart',
        ...props,
    }
    let initialConfig = {}
    if (props.initialConfig) {
        if (typeof props.initialConfig === 'string') {
            try {
                initialConfig = JSON.parse(props.initialConfig)
            } catch (e) {
                logger.error('Failed to parse initialConfig string', e)
            }
        } else {
            initialConfig = props.initialConfig
        }
    }

    const dispatch = useAppDispatch()
    const queryClient = useQueryClient()
    const config = useAppSelector((state: RootState) => state.admin.config)
    const query = useAppSelector((state: RootState) => state.app.queries[QueryType.Analytics])
    const organization = useAppSelector((state: RootState) => state.admin.organization)
    const user = useAppSelector((state: RootState) => state.user.data)
    const canEditAIContext = Boolean(user?.roles?.includes(UserRole.AppsEdit))

    const [loaded, setLoaded] = useState(0)
    const [data, setData] = useState(null)
    const [maps, setMaps] = useState({})
    const [listOrder, setListOrder] = useState<[string | null, boolean]>(
        props.order ? props.order : [null, true],
    )
    const scrollRef = useRef()
    const [definitions, setDefinitions] = useState(null)
    const [ranges, setRanges] = useState({})
    const [options, setOptions] = useState(null)
    const [expand, setExpand] = useState(null)
    const [search, setSearch] = useLocalStorage(
        'search',
        [],
    ) as [
        AnalyticsSearchFilter[],
        React.Dispatch<React.SetStateAction<AnalyticsSearchFilter[]>>,
    ]
    const [expandMap, setExpandMap] = useState({})
    const [parseMap, setParseMap] = useState({})
    const [readOnly, setReadOnly] = useState(props.readOnly || false)
    const [tableName, setTableName] = useState(initialConfig.name || props.name || '')
    const [description, setDescription] = useState(initialConfig.description || '')
    const [iconName, setIconName] = useState(initialConfig.icon || 'fas fa-chart-bar')
    const [manualRefreshInFlight, setManualRefreshInFlight] = useState(false)
    const tableNameRef = useRef(tableName)
    const loadedTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Flag to track if we've applied initial config
    const [initialConfigApplied, setInitialConfigApplied] = useState(false)

    // New state for view mode (table or chart)
    const [viewMode, setViewMode] = useState(defaultViewMode || 'table')
    const [showInfo, setShowInfo] = useState(false)

    // AI Analysis state
    const [aiAnalysis, setAiAnalysis] = useState(null)
    const [aiLoading, setAiLoading] = useState(false)
    const [showAIPanel, setShowAIPanel] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [showAIContextEditor, setShowAIContextEditor] = useState(false)

    // Chart drilldown state
    const [drilldownPoint, setDrilldownPoint] = useState<AnalyticsChartPointSelection | null>(null)

    // Chart type state
    const [chartType, setChartType] = useState(null)

    const showWizardOnStart = (
        (Object.keys(initialConfig).length <= 1 || !initialConfig.viewMode)
        && !skipWizard
        && !props.wizardTemplate
    )

    // Wizard related states
    const [showWizard, setShowWizard] = useState(showWizardOnStart)
    const [selectedWizardOptions, setSelectedWizardOptions] = useState(
        initialConfig.wizardOptions || {},
    )
    const [wizardCompleted, setWizardCompleted] = useState(!showWizardOnStart || skipWizard)
    const [activeTemplate, setActiveTemplate] = useState(null)

    // Transition state
    const [isTransitioning, setIsTransitioning] = useState(false)
    const previousCompactMode = useRef(compactMode)

    const searchTimeout = useRef(null)
    const [optionalFiltersEnabled, setOptionalFiltersEnabled] = useLocalStorage(
        `optionalFiltersEnabled_${props.type || 'analytics'}`,
        initialConfig.optionalFiltersEnabled || {},
    ) as [
        Record<string, boolean>,
        React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    ]
    const type = props.type
        || (definitions && options?.aggregation
            ? definitions[options.aggregation]?.category
            : Analytics.Pages)
    const prevOptions = useRef(options)
    const changeMade = useRef(false)

    // Optional filters, finalized based on expand map
    const optionalFilters = props.optionalFilters ? [...props.optionalFilters] : []
    const expandedFilters = []
    Object.keys(expandMap).forEach((x) => {
        Object.keys(expandMap[x]).forEach((y) => {
            optionalFilters.push(y)
            expandedFilters.push(y)
        })
    })
    const optionalSet = new Set(optionalFilters)

    const previousConfigRef = useRef(null)
    const createdRef = useRef(false)
    const [cancelled, setCancelled] = useState(false)

    function fieldKey(field: AnalyticsDefinitionField) {
        let { key } = field
        if (field.map) {
            if (Array.isArray(field.map)) {
                key = `${key}-${field.map.join(',')}`
            } else {
                key = `${key}-${field.map}`
            }
        }

        if (field.childKey) {
            key = `${key}-${field.childKey}`
        }
        return key
    }

    // Initialize wizard template - but only if not skipping wizard
    useEffect(() => {
        if (skipWizard) {
            setActiveTemplate(null)
            setWizardCompleted(true)
            return
        }

        if (wizardTemplate) {
            // If wizardTemplate is a string, look it up in the templates
            if (typeof wizardTemplate === 'string') {
                if (wizardTemplate in wizardTemplates) {
                    setActiveTemplate(wizardTemplates[wizardTemplate])
                } else {
                    logger.error(`Wizard template '${wizardTemplate}' not found`)
                    // Fall back to the first available template
                    setActiveTemplate(Object.values(wizardTemplates)[0])
                }
            } else if (typeof wizardTemplate === 'object') {
                // Use directly provided template object
                setActiveTemplate(wizardTemplate)
            }
        } else if (!skipWizard) {
            // Default to first template if none specified and not skipping
            setActiveTemplate(Object.values(wizardTemplates)[0])
        }
    }, [wizardTemplate, skipWizard])

    useEffect(() => {
        if (!props.definitions) {
            return
        }

        const newDefinitions = fnc.copyObj(props.definitions)

        // Remove app fields when using an app
        if (app != null) {
            Object.keys(props.definitions).forEach((x) => {
                const projectIdx = newDefinitions[x].fields.findIndex((y) => y.key === 'appId')
                if (projectIdx !== -1) {
                    newDefinitions[x].fields.splice(projectIdx, 1)
                }
            })
        }
        Object.keys(props.definitions).forEach((x) => {
            // Make sure fields is distint (remove duplicates)
            newDefinitions[x].fields = newDefinitions[x].fields.filter(
                (v, i, a) => a.findIndex((t) => {
                    const keyA = fieldKey(t)
                    const keyB = fieldKey(v)
                    return keyA === keyB
                }) === i,
            )
        })
        setDefinitions(newDefinitions)

        let defaultOptions = {
            aggregation: initialConfig.aggregation || Object.keys(newDefinitions)[0],
            page: 0,
            count: 25,
        }

        // Apply any other initial options from config
        if (initialConfig.options) {
            defaultOptions = {
                ...defaultOptions,
                ...initialConfig.options,
            }
        }

        setOptions(defaultOptions)

        const minDate = props.allTime ? new Date(0) : new Date()
        if (!props.allTime) {
            minDate.setFullYear(minDate.getFullYear() - 1)
        }
        minDate.setHours(0, 0, 0, 0)
        const maxDate = new Date()
        maxDate.setHours(23, 59, 59, 0)
        setRanges({ [AnalyticsFilter.Date]: { min: minDate, max: maxDate } })
    }, [])

    // Apply initial configuration if available
    useEffect(() => {
        if (initialConfigApplied) {
            return
        }
        if (Object.keys(initialConfig).length > 0 && definitions && options) {
            // Apply saved order if any
            if (initialConfig.order) {
                setListOrder(initialConfig.order)
            }

            // Apply saved view mode
            if (initialConfig.viewMode) {
                setViewMode(initialConfig.viewMode)
            }

            // Apply saved chart type (only valid chart types, not view modes like 'table')
            const validChartTypes = ['line', 'bar', 'pie', 'top', 'funnel', 'stacked-funnel']
            if (initialConfig.chartType && validChartTypes.includes(initialConfig.chartType)) {
                setChartType(initialConfig.chartType || definitions[options.aggregation]?.chartType)
            }

            if (initialConfig.wizardOptions) {
                setSelectedWizardOptions(initialConfig.wizardOptions)
            }

            // Apply saved name if any
            if (initialConfig.name) {
                setTableName(initialConfig.name)
            } else {
                setTableName(definitions[options.aggregation]?.label || 'Analytics')
            }

            // Apply saved query filters if any — strip any saved date so the session date persists
            if (initialConfig.query && Object.keys(initialConfig.query).length > 0) {
                const queryWithoutDate = { ...initialConfig.query }
                delete queryWithoutDate[AnalyticsFilter.Date]
                delete queryWithoutDate[AnalyticsFilter.MonthRange]
                dispatch(applyQuery({
                    query: {
                        ...queryWithoutDate,
                        [AnalyticsFilter.Date]: query?.[AnalyticsFilter.Date],
                    },
                    type: QueryType.Analytics,
                }))
            }

            // Mark config as applied
            setInitialConfigApplied(true)
        } else if (definitions && options) {
            // If no initial config, set default name based on aggregation
            setTableName(definitions[options.aggregation]?.label || 'Analytics')
            setChartType(definitions[options.aggregation]?.chartType)
        }
    }, [initialConfig, definitions, options])

    useEffect(() => {
        setReadOnly(props.readOnly)
    }, [props.readOnly])

    useEffect(() => {
        if (!changeMade.current) {
            return
        }
        changeMade.current = false
        const finalDescription = description || ''
        const finalIcon = iconName || 'fas fa-chart-bar'
        if (props.onChange || props.onCreate) {
            const currentConfig = {
                ...initialConfig,
                aggregation: options?.aggregation,
                /* options: {
                    ...options,
                    page: 0
                }, */
                order: listOrder,
                viewMode,
                chartType,
                query,
                // wizardOptions: selectedWizardOptions,
                optionalFiltersEnabled,
                // chartOptions: updatedChartOptions,
                name: tableName,
                description: finalDescription,
                icon: finalIcon,
            }
            if (JSON.stringify(currentConfig) !== JSON.stringify(previousConfigRef.current)) {
                if (props.onCreate && createdRef.current) {
                    props.onCreate(currentConfig)
                } else if (props.onChange) {
                    props.onChange(currentConfig)
                }
                createdRef.current = false
                previousConfigRef.current = currentConfig
            }
        }
    }, [
        selectedWizardOptions,
        optionalFiltersEnabled,
        viewMode,
        chartType,
        listOrder,
        tableName,
        description,
        iconName,
    ])

    // Handle transitions between compact and non-compact modes
    useEffect(() => {
        if (previousCompactMode.current !== compactMode) {
            // Set transitioning state to true when compact mode changes
            setIsTransitioning(true)

            // After animation completes (300ms is a common duration), reset the transitioning state
            const transitionTimeout = setTimeout(() => {
                setIsTransitioning(false)
            }, 1000)

            // Update previous state
            previousCompactMode.current = compactMode

            // Clear timeout on unmount
            return () => clearTimeout(transitionTimeout)
        }

        return undefined
    }, [compactMode])

    const handleAddToDashboard = (dashboardId) => {
        if (onAddToDashboard && (previousConfigRef.current || initialConfig)) {
            onAddToDashboard(
                dashboardId,
                previousConfigRef.current ? previousConfigRef.current : initialConfig,
            )
        }
    }

    function handleTileOperation(operation, payload) {
        if (props.onTileOperation) {
            props.onTileOperation(operation, payload)
        }
    }

    function handleTableName(x) {
        tableNameRef.current = x
        changeMade.current = true
        setTableName(x)
    }

    function handleDescription(x) {
        changeMade.current = true
        setDescription(x)
    }

    function handleIconName(x) {
        changeMade.current = true
        setIconName(x)
    }

    // Handler for wizard completion
    const handleWizardComplete = (selectedOptions, aggregation) => {
        changeMade.current = true
        createdRef.current = true
        setSelectedWizardOptions(selectedOptions)
        setWizardCompleted(true)
        setShowWizard(false)

        // Apply title/icon/chart defaults from the last selected wizard option.
        if (selectedOptions && Object.keys(selectedOptions).length > 0) {
            const wizardValues = Object.values(selectedOptions)
            const lastOption = wizardValues.slice(-1)[0]
            if (lastOption) {
                handleDescription(lastOption.description || '')
                handleIconName(lastOption.icon || 'fas fa-chart-bar')
                handleTableName(lastOption.title || tableName)
                setChartType(lastOption.chartType || definitions[aggregation]?.chartType || null)
                setViewMode(lastOption.viewMode || defaultViewMode || 'chart')
            }
        }

        // Enable columns specified in the wizard selections
        const enabledColumns = new Set()

        // Process each selected option to gather columns to enable
        Object.values(selectedOptions).forEach((option: Dict) => {
            if (option.columns) {
                const columns = Array.isArray(option.columns) ? option.columns : []
                columns.forEach((column: string) => enabledColumns.add(column))
            }
        })

        // Update the optionalFiltersEnabled state to show the columns from the wizard
        const newOptionalFiltersEnabled = {}
        enabledColumns.forEach((column) => {
            newOptionalFiltersEnabled[column] = true
        })
        setOptionalFiltersEnabled(newOptionalFiltersEnabled)

        // Set aggregation if provided
        if (aggregation && definitions && aggregation in definitions) {
            logger.info(`Setting aggregation to ${String(aggregation)}`)
            const newOptions = {
                ...options,
                aggregation, // Use shorthand property
                page: 0, // Reset to first page
            }
            const aggDef = definitions[aggregation]
            if ('order' in aggDef) {
                setListOrder(aggDef.order)
            }
            setOptions(newOptions)
        } else if (options) {
            // Just reset to first page if no aggregation
            const newOptions = { ...options, page: 0 }
            setOptions(newOptions)
        }
    }

    // Handler for wizard cancellation
    const handleWizardCancel = () => {
        if (props.onWizardCancel) {
            props.onWizardCancel()
            return
        }

        setShowWizard(false)

        // If we have no initial config, we need to set a default completed state
        // to avoid a blank render
        if (Object.keys(initialConfig).length === 0) {
            setWizardCompleted(true)

            // Set default options if needed
            if (definitions && options) {
                const defaultAggregation = options.aggregation || Object.keys(definitions)[0]
                setOptions({
                    ...options,
                    aggregation: defaultAggregation,
                    page: 0,
                })

                // Set default table name
                setTableName(definitions[defaultAggregation]?.label || 'Analytics')
            }
        } else {
            // Use existing config
            setWizardCompleted(true)
        }

        // Set loaded state to trigger the view render
        setLoaded(0)
    }

    // Reset wizard handler
    const handleResetWizard = () => {
        setShowWizard(true)
        setWizardCompleted(false)
        setSelectedWizardOptions({})
        setLoaded(0)

        // Clear filters but preserve the current session date
        dispatch(applyQuery({
            query: { [AnalyticsFilter.Date]: query?.[AnalyticsFilter.Date] },
            type: QueryType.Analytics,
        }))

        // Reset optional filters
        setOptionalFiltersEnabled({})
    }

    function handleSearch(key, value) {
        // Check for key
        const idx = search.findIndex((x) => x.key === key)
        const newSearch = [...search]
        if (idx != -1) {
            if (value == null) {
                newSearch.splice(idx, 1)
            } else {
                newSearch[idx].value = value
            }
            setSearch(newSearch)
            clearTimeout(searchTimeout.current)
            searchTimeout.current = setTimeout(() => {
                const newOptions = { ...options }
                newOptions.search = newSearch
                setOptions(newOptions)
            }, 1000)
        }
    }

    function handleNewSearch(key, label) {
        const newSearch = [...search]
        newSearch.push({ label, key, value: null })
        setSearch(newSearch)
    }

    function handleOptionalFilter(x) {
        const newFilters = fnc.copyObj(optionalFiltersEnabled)
        newFilters[x] = !newFilters[x]
        if (!newFilters[x]) {
            delete newFilters[x]
        }
        setOptionalFiltersEnabled(newFilters)
    }

    function handleToggleReadOnly() {
        setReadOnly(!readOnly)

        // If switching from read-only to edit mode and we have an onEdit callback, call it
        if (readOnly && props.onEdit) {
            props.onEdit()
        }
    }

    function handleChartType(newChartType) {
        setChartType(newChartType)
        changeMade.current = true
    }

    function parseMaps(rawMaps) {
        let newMaps = {}
        Object.keys(rawMaps).forEach((x) => {
            if (rawMaps && rawMaps[x]) {
                newMaps[x] = rawMaps[x].reduce((acc, y) => ({ ...acc, [y.id]: y }), {})
            }
        })
        // Custom maps
        if (additionalMaps) {
            newMaps = { ...newMaps, ...additionalMaps }
        }
        return newMaps
    }

    function nextPage() {
        if (!options) {
            return
        }
        const page = Number(options.page) || 0
        setOptions({ ...options, page: page + 1 })
    }
    function prevPage() {
        if (!options) {
            return
        }
        const page = Number(options.page) || 0
        if (page > 0) {
            setOptions({ ...options, page: page - 1 })
        }
    }

    function getFields() {
        if (options && options.aggregation in definitions) {
            const finalFields = []
            const fieldSet = new Set()
            definitions[options.aggregation].fields.forEach((x) => {
                // Skip hidden fields from display
                if (x.hidden) {
                    return
                }
                if (optionalSet.has(x.key) && !(x.key in optionalFiltersEnabled) && !search.find((y) => y.key === x.key) && !query[x.key]) {
                    return false
                }
                const key = fieldKey(x)
                if (x.expand) {
                    // Look for fields in expandMap
                    if (x.key in expandMap) {
                        Object.keys(expandMap[x.key]).forEach((y) => {
                            if (optionalSet.has(y) && !(y in optionalFiltersEnabled)) {
                                return false
                            }
                            finalFields.push({ label: y, key: x.key, childKey: y })
                        })
                    }
                } else if (!fieldSet.has(key)) {
                    finalFields.push(x)
                    fieldSet.add(key)
                }
            })
            return finalFields
        }
        return []
    }

    const analyticsDefinition = options?.aggregation && definitions
        ? definitions[options.aggregation]
        : null
    const currentFields = useMemo(
        () => getFields(),
        [definitions, options, expandMap, optionalFiltersEnabled, search, query],
    )
    const analyticsRequestArgs = useMemo(() => {
        if (!options || !analyticsDefinition) {
            return null
        }

        return {
            app,
            organization,
            type,
            options,
            query,
            filters,
            ranges,
            search,
            listOrder,
            definition: analyticsDefinition,
            aggregation: options.aggregation,
            fields: currentFields,
        }
    }, [
        app,
        organization,
        type,
        options,
        query,
        filters,
        ranges,
        search,
        listOrder,
        analyticsDefinition,
        currentFields,
    ])
    const analyticsEnabled = Boolean(
        query != null
        && options != null
        && organization
        && !(showWizard && !wizardCompleted && !skipWizard),
    )
    const analyticsQuery = useAnalyticsData({
        requestArgs: analyticsRequestArgs,
        enabled: analyticsEnabled,
    })
    const analytics = analyticsQuery.data ?? null
    const querying = analyticsQuery.isFetching || manualRefreshInFlight

    useEffect(() => {
        if (
            definitions == null
            || analytics == null
            || Object.keys(analytics).length <= 0
            || !options
        ) {
            return
        }

        setMaps(analytics.maps ? parseMaps(analytics.maps) : [])
        setData(analytics)

        const expandableFields = definitions[options.aggregation].fields.filter((x) => x.expand)
        const newExpandMap = {}
        const newParseMap = {}

        expandableFields.forEach((x) => {
            newExpandMap[x.key] = {}
            analytics[type]?.forEach((y) => {
                if (x.key in y) {
                    const message = parseBody(null, null, y[x.key])
                    if (!(y.id in newParseMap)) {
                        newParseMap[y.id] = {}
                    }

                    const parseData = {}
                    Object.keys(message).forEach((z) => {
                        const field = z.toLowerCase().toReadable()
                        if (z.toLowerCase().includes('utm')) {
                            return
                        }

                        parseData[field] = message[z]
                        if (!(field in newExpandMap[x.key])) {
                            newExpandMap[x.key][field] = 1
                        }
                    })
                    newParseMap[y.id][x.key] = parseData
                }
            })
        })

        setExpandMap(newExpandMap)
        setParseMap(newParseMap)
    }, [analytics, definitions, options, type])

    useEffect(() => {
        if (prevOptions.current && options && prevOptions.current.aggregation !== options.aggregation) {
            setLoaded(0)
        }
    }, [options?.aggregation])

    useEffect(() => {
        if (!analytics) {
            return
        }

        prevOptions.current = options
        setCancelled(false)

        if (loaded !== 0) {
            return
        }

        setLoaded(1)
        if (loadedTimerRef.current) {
            clearTimeout(loadedTimerRef.current)
        }
        loadedTimerRef.current = setTimeout(() => {
            setLoaded(2)
        }, 500)
    }, [analytics, analyticsQuery.dataUpdatedAt, options])

    useEffect(() => () => {
        if (loadedTimerRef.current) {
            clearTimeout(loadedTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (!analyticsQuery.error) {
            return
        }

        logger.error(analyticsQuery.error)
        if (!analytics) {
            setData({
                error: analyticsQuery.error instanceof Error
                    ? analyticsQuery.error.message
                    : 'Failed to load analytics',
            })
        }
    }, [analyticsQuery.error, analytics])

    useEffect(() => {
        if (analyticsQuery.isFetching) {
            setCancelled(false)
        }
    }, [analyticsQuery.isFetching])

    async function handleCancelQuery() {
        if (props.onCancel) {
            props.onCancel()
        }

        if (analyticsQuery.request?.queryKey) {
            await queryClient.cancelQueries({ queryKey: analyticsQuery.request.queryKey })
        }
        setManualRefreshInFlight(false)
        setCancelled(true)
    }

    async function handleRefresh() {
        if (!analyticsRequestArgs || !analyticsQuery.request) {
            return
        }

        const refreshRequest = buildAnalyticsRequest({
            ...analyticsRequestArgs,
            nocache: true,
        })

        if (!refreshRequest) {
            return
        }

        setCancelled(false)
        setManualRefreshInFlight(true)
        try {
            await queryClient.fetchQuery({
                queryKey: analyticsQuery.request.queryKey,
                queryFn: ({ signal }) => fetchAnalyticsRequest(refreshRequest, app, signal),
                staleTime: 0,
            })
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
                logger.info('Analytics query cancelled')
                setCancelled(true)
            } else {
                logger.error(err)
            }
        } finally {
            setManualRefreshInFlight(false)
        }
    }

    async function retrieveData(csv = false, nocache = false) {
        if (!analyticsRequestArgs) {
            return
        }

        const request = buildAnalyticsRequest({
            ...analyticsRequestArgs,
            csv,
            nocache,
        })

        if (!request) {
            return
        }

        const finalFields = getFields()
        try {
            const ret = await fetchAnalyticsRequest(request, app)

            if (csv) {
                const rawMaps = ret?.maps || ret?.payload?.maps || {}
                const rawData = ret?.[type] || ret?.payload?.[type] || []
                const parsedMaps = parseMaps(rawMaps)
                const csvData = []
                const fields = Object.values(finalFields)
                const headers = []

                fields.forEach((x) => {
                    if (x.key in expandMap) {
                        Object.keys(expandMap[x.key]).forEach((y) => {
                            if (optionalSet.has(y) && !optionalFiltersEnabled[y]) {
                                return
                            }
                            headers.push(y)
                        })
                    } else {
                        headers.push(x.label)
                    }
                })

                if (rawData.length > 0) {
                    rawData.forEach((x) => {
                        const row = []
                        fields.forEach((y) => {
                            if (y.key in expandMap) {
                                Object.keys(expandMap[y.key]).forEach((z) => {
                                    if (optionalSet.has(z) && !optionalFiltersEnabled[z]) {
                                        return
                                    }
                                    const parsed = parseBody(y.id, y.key, x[y.key])
                                    row.push(parsed[z])
                                })
                                return
                            }

                            const value = x[y.key]
                            if (parsedMaps && y.key in parsedMaps && value in parsedMaps[y.key]) {
                                row.push(parsedMaps[y.key][value].label)
                                return
                            }
                            row.push(value)
                        })
                        csvData.push(row)
                    })
                }

                downloadCSV({
                    filename: `${type}-analytics_${fnc.timestamp('YYYY-MM-DD')}.csv`,
                    headers,
                    data: csvData,
                })
                return ret
            }

            queryClient.setQueryData(analyticsQuery.request?.queryKey || request.queryKey, ret)
            return ret
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
                logger.info('Analytics query cancelled')
                setCancelled(true)
            } else {
                logger.error(err)
            }
        }
    }

    function handleAggregation(x) {
        const nextOptions = { ...options, aggregation: x, page: 0 }
        const aggDef = definitions?.[x]
        if (aggDef?.order) {
            setListOrder(aggDef.order)
        }
        setOptions(nextOptions)
    }

    async function handleAIAnalysis() {
        if (!analyticsRequestArgs) {
            return
        }

        setAiLoading(true)
        setAiError(null)
        setShowAIPanel(true)
        try {
            const request = buildAnalyticsAIRequest(analyticsRequestArgs)
            if (!request) {
                setAiError('Unable to build AI analysis request.')
                return
            }

            const result = await queryClient.fetchQuery({
                queryKey: request.queryKey,
                queryFn: ({ signal }) => fetchAnalyticsAIRequest(request, app, signal),
                staleTime: 10 * 60 * 1000,
            })

            if (result?.analysis) {
                setAiAnalysis(result.analysis)
            } else {
                setAiError('No analysis returned. Please try again.')
            }
        } catch (e) {
            logger.error('AI analysis failed', e)
            setAiError('AI analysis failed. Please try again.')
        } finally {
            setAiLoading(false)
        }
    }

    function resetScroll() {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0
        }
    }

    function toggleSort(key: string) {
        if (listOrder[0] === key) {
            setListOrder([listOrder[0], !listOrder[1]])
        } else {
            setListOrder([key, true])
        }
        resetScroll()
        changeMade.current = true
    }

    function setFilter(filter, value) {
        // Check if is a expanded filter value
        const newQuery = setQueryHelper(QueryType.Analytics, query, { [filter]: [value] })
        dispatch(applyQuery({ query: newQuery, type: QueryType.Analytics }))
    }

    function parseBody(id, key, value) {
        if (id && key && id in parseMap && key in parseMap[id]) {
            return parseMap[id][key]
        }

        const message = {}
        const parseMessageLines = (rawValue) => {
            if (typeof rawValue !== 'string') {
                return
            }

            rawValue.replaceAll('<br>', '\n\n').replaceAll('<br/>', '\n\n').split('\n\n').forEach((line) => {
                const separatorIndex = line.indexOf(':')
                // Older submissions can contain a plain free-form message. It
                // has no label/value delimiter and must not break the table.
                if (separatorIndex <= 0) {
                    return
                }

                const label = line.slice(0, separatorIndex).trim().toLowerCase().toReadable()
                const lineValue = line.slice(separatorIndex + 1).trim()
                if (label) {
                    message[label] = lineValue
                }
            })
        }

        // Check if value is a JSON string.
        let isJson = false
        try {
            const jsonValue = JSON.parse(value)
            isJson = true
            if (jsonValue && typeof jsonValue === 'object') {
                if (jsonValue.message) {
                    parseMessageLines(jsonValue.message)
                } else {
                    Object.keys(jsonValue).forEach((jsonKey) => {
                        message[jsonKey.toReadable()] = jsonValue[jsonKey]
                    })
                }
            }
        } catch (e) {
            // Legacy data_message values are often plain HTML/text, not JSON.
        }
        if (!isJson) {
            parseMessageLines(value)
        }
        return message
    }

    function fieldMap(x, fields, index) {
        const {
            childKey, label, key, na, alt, map, num, placeholder, valueMap,
        } = fields

        let value = alt ? x[alt] : x[key]
        let displayValue = value
        let filterValue = value
        const filterableFields = new Set(filters)
        const style = {}
        const styleB = {}
        switch (key) {
            case 'availabilityStateId':
            case 'prevAvailabilityStateId':
                if (key in maps && x[key] in maps[key]) {
                    const state = maps[key][x[key]]
                    if (state) {
                        value = displayValue = state.label
                        // style.padding = '5px'
                        styleB.padding = '10px'
                        styleB.borderRadius = '5px'
                        styleB.backgroundColor = `#${state.color}`
                        styleB.color = 'white'
                        styleB.display = 'flex'
                        styleB.justifyContent = 'center'
                    }
                }
                break
            case 'totalDuration':
                value = displayValue = filterValue = `${Math.floor(10 * parseInt(x.totalDuration) / 1000) / 10}s`
                break
            case 'optIn':
                value = displayValue = filterValue = value ? 'Yes' : 'No'
                break
            case 'dateVisited':
            case 'dateSent':
            case 'dateChanged':
                value = displayValue = filterValue = fnc.dateFriendly(new Date(x[key]), { time: true })
                break
            case 'dataMessage':
                const messageData = parseBody(x.id, key, value)
                if (childKey) {
                    if (childKey in messageData) {
                        value = messageData[childKey]
                    } else {
                        value = 'N/A'
                    }
                } else {
                    value = ''
                    Object.keys(messageData).forEach((key) => {
                        value += `${key.toReadable()}: ${messageData[key]}\n\n`
                    })
                }
                displayValue = filterValue = value
                break
            default:
                // Special case to get things like user id from intermediary table
                const ret = analyticsFieldValue(value, fields, x, maps, config)
                value = ret.value
                displayValue = ret.displayValue
                filterValue = ret.filterValue
                break
        }

        // Format value based on field type
        if (fields.type === FieldType.Currency && (value || value === 0)) {
            displayValue = fnc.toMoney(value, false)
        } else if (fields.type === FieldType.Integer && (value || value === 0)) {
            displayValue = parseInt(value).toLocaleString('en-US')
        } else if (fields.type === FieldType.Float && (value || value === 0)) {
            displayValue = parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }

        if (fields.action) {
            const { action, items } = fields
            return <td key={key} style={style}><DropdownMenu buttonClass="no-bg" items={items} onChange={(y) => action(y, fields, x)} icon={icons.ellipsisH} /></td>
        }

        const expandKey = uniqueKey
        const expanded = expand == expandKey
        const uniqueKey = fieldKey(x)
        let text = displayValue
        if (!expanded && text && text.length > 20 && !key.includes('date')) {
            text = `${text.slice(0, 20)}...`
        }
        if ((!text || text.length == 0) && placeholder) {
            text = placeholder
        }
        if (value || value === 0) {
            let finalKey = key
            if (map) {
                if (Array.isArray(map) && map.length > 0) {
                    finalKey = map[0]
                } else {
                    finalKey = map
                }
            }
            return <td key={uniqueKey} style={style} onClick={() => setExpand(expanded ? null : expandKey)}>
                {filterable && filterableFields.has(finalKey) && <IconButton noBg className="inline-filter" icon="fas fa-filter" onClick={(e) => {
                    e.stopPropagation()
                    const finalValue = filterValue
                    if (childKey) {
                        setFilter(`${finalKey}_${childKey}`, finalValue)
                    } else {
                        setFilter(finalKey, map ? finalValue : x[key])
                    }
                }} />}
                <span style={styleB}><ReactMarkdown>{text.toString()}</ReactMarkdown></span>
            </td>
        }
        return <td key={key} style={style} onClick={() => setExpand(expanded ? null : expandKey)}><span style={styleB}>{na || 'N/A'}</span></td>
    }

    function dataMap(x, ix) {
        return <tr key={ix} onClick={onClick ? () => onClick(x) : null} className={onClick ? 'clickable' : ''}>
            {getFields().map((y) => fieldMap(x, y, ix))}
        </tr>
    }

    function getSortHeader(header) {
        const {
            key, childKey, label, map, action, tooltip,
        } = header
        const tooltipIcon = tooltip ? <IconButton noBg icon="fas fa-info-circle" className="header-tooltip-icon" tooltip={tooltip} onClick={(e) => e.stopPropagation()} /> : null
        // Don't sort if relying on secondary map
        if (map != null || childKey || action) {
            return <th key={`${key}-${childKey}`} field={key} className="sort-header noselect" data-field={key}>
                <span className="sort-label">{label}</span>{tooltipIcon}
            </th>
        }

        let downIcon = 'fas fa-chevron-down'
        let upIcon = 'fas fa-chevron-up'
        if (listOrder[0] === key) {
            if (listOrder[1]) {
                downIcon = 'fas fa-chevron-circle-down'
            } else {
                upIcon = 'fas fa-chevron-circle-up'
            }
        }
        return <th key={key} field={key} onClick={() => toggleSort(key)} className="sort-header noselect" data-field={key}>
            <div className="sort-wrapper">
                <span className="sort-label">{label}</span>
                {tooltipIcon}
                <div className="sort-buttons">
                    <Icon noBg icon={upIcon} />
                    <Icon noBg icon={downIcon} />
                </div>
            </div>
        </th>
    }

    function formatInvertedCell(value: any, field: any): string {
        if (value === null || value === undefined) return 'N/A'
        if (field.type === FieldType.Currency) return fnc.toMoney(value, false)
        if (field.type === FieldType.Integer) return parseInt(value).toLocaleString('en-US')
        if (field.type === FieldType.Float) return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        return String(value)
    }

    function renderInvertedTableBlock(rows: any[], metricFields: any[], columnField: any) {
        // Each row is one column (one month); each metricField becomes a row
        return (
            <table className="analytics-table-inverted">
                <thead>
                    <tr>
                        <th className="inverted-label" />
                        {rows.map((row, i) => (
                            <th key={i} className="inverted-col-header">{row[columnField.key]}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {metricFields.map((field) => (
                        <tr key={field.key}>
                            <th className="inverted-label">
                                {field.label}
                                {field.tooltip && <IconButton noBg icon="fas fa-info-circle" className="header-tooltip-icon" tooltip={field.tooltip} onClick={(e) => e.stopPropagation()} />}
                            </th>
                            {rows.map((row, i) => (
                                <td key={i}>{formatInvertedCell(row[field.key], field)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        )
    }

    function renderInvertedTable() {
        const currentDef = definitions[options.aggregation]
        const allFields = currentDef?.fields ?? []
        const pivotGroupBy: string | undefined = currentDef?.pivotGroupBy
        const columnField = allFields.find((f) => f.pivotColumn)
        const metricFields = allFields.filter((f) => !f.hidden && !f.pivotColumn)

        if (!columnField) {
            // Fallback: original flat inverted rendering (no column header row)
            return (
                <table className="analytics-table-inverted">
                    <tbody>
                        {metricFields.map((field) => (
                            <tr key={field.key}>
                                <th className="inverted-label">
                                    {field.label}
                                    {field.tooltip && <IconButton noBg icon="fas fa-info-circle" className="header-tooltip-icon" tooltip={field.tooltip} onClick={(e) => e.stopPropagation()} />}
                                </th>
                                {data[type].map((row, i) => (
                                    <td key={i}>{formatInvertedCell(row[field.key], field)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
        }

        if (pivotGroupBy) {
            // Group rows by the pivotGroupBy key, preserving insertion order
            const groups = new Map<string, any[]>()
            data[type].forEach((row) => {
                const groupKey = row[pivotGroupBy] ?? ''
                if (!groups.has(groupKey)) groups.set(groupKey, [])
                groups.get(groupKey).push(row)
            })

            return (
                <div className="analytics-pivot-groups">
                    {Array.from(groups.entries()).map(([groupName, rows]) => (
                        <div key={groupName} className="analytics-pivot-group">
                            <div className="analytics-pivot-group-header">{groupName}</div>
                            {renderInvertedTableBlock(rows, metricFields, columnField)}
                        </div>
                    ))}
                </div>
            )
        }

        // Single inverted table with column headers (e.g. full_funnel_monthly)
        return renderInvertedTableBlock(data[type], metricFields, columnField)
    }

    function getSortedRows() {
        if (!data?.[type] || !Array.isArray(data[type])) {
            return []
        }

        const aggDef = definitions?.[options?.aggregation]
        const sortableChartTypes = new Set([AnalyticsChartType.BarChart, AnalyticsChartType.TopChart])
        if (!aggDef || !sortableChartTypes.has(aggDef.chartType)) {
            return data[type]
        }

        const currentOrder = (listOrder && listOrder[0])
            ? listOrder
            : (aggDef.order || [null, true])

        const [sortKey, descFlag] = currentOrder || []
        if (!sortKey) {
            return data[type]
        }

        const desc = descFlag !== false
        const rows = [...data[type]]
        rows.sort((a, b) => {
            const valA = a?.[sortKey]
            const valB = b?.[sortKey]

            const numA = Number(valA)
            const numB = Number(valB)
            const bothNumeric = Number.isFinite(numA) && Number.isFinite(numB)
            if (bothNumeric) {
                return desc ? numB - numA : numA - numB
            }

            const strA = (valA ?? '').toString()
            const strB = (valB ?? '').toString()
            return desc ? strB.localeCompare(strA) : strA.localeCompare(strB)
        })
        return rows
    }

    function getData() {
        const sortedRows = getSortedRows()
        if (sortedRows && Array.isArray(sortedRows)) {
            if (sortedRows.length > 0) {
                return sortedRows.map(dataMap)
            }
            return <tr><td colSpan={definitions[options.aggregation].fields.length}>No data </td></tr>
        }
        return null
    }

    function toggleViewMode() {
        const newViewMode = viewMode === AnalyticsViewMode.Table ? AnalyticsViewMode.Chart : AnalyticsViewMode.Table
        setViewMode(newViewMode)
        changeMade.current = true
    }

    // Use month-range picker for monthly aggregations; fall back to initialConfig aggregation before options load
    const currentAggregation = options?.aggregation ?? initialConfig?.aggregation ?? ''
    const activeDateFilter = MONTHLY_AGGREGATIONS.has(currentAggregation)
        ? AnalyticsFilter.MonthRange
        : AnalyticsFilter.Date

    function getDateFilter() {
        return <FilterOptions
            filters={[activeDateFilter]}
            queryType={QueryType.Analytics}
            ranges={ranges}>
        </FilterOptions>
    }

    function getChartTypeDropdown() {
        if (viewMode !== AnalyticsViewMode.Chart) {
            return null
        }
        const chartIcons = {
            line: 'fas fa-chart-line',
            bar: 'fas fa-chart-bar',
            pie: 'fas fa-chart-pie',
            top: 'fas fa-trophy',
        }

        return (
            <DropdownMenu
                selectText="Chart Type"
                icon={chartIcons[chartType] || chartIcons.bar}
                text={chartType ? chartType.toReadable() : 'Chart Type'}
                value={chartType || 'bar'}
                onChange={handleChartType}
                items={[
                    { value: 'bar', text: 'Bar Chart', icon: chartIcons.bar },
                    { value: 'pie', text: 'Pie Chart', icon: chartIcons.pie },
                    { value: 'top', text: 'Top Items', icon: chartIcons.top },
                ]}
            />
        )
    }

    function getAnalyticView() {
        // if (!data) {
        //     return <h3>No Analytics To Show</h3>
        // }

        if (!maps || !definitions) {
            return null
        }
        let optionalItems = Object.keys(AnalyticsFilter).map((x) => ({ text: x, value: AnalyticsFilter[x] })).filter((x) => optionalSet.has(x.value))
        if (expandedFilters.length > 0) {
            const expandedItems = expandedFilters.map((x) => ({ text: `${x} (extra)`, value: x }))
            optionalItems = [...expandedItems, ...optionalItems]
        }

        const searchItems = searchFilters.map((x) => {
            if (typeof x === 'object') {
                return x
            }
            return { text: x.toReadable(), value: x }
        })

        let title = 'Analytics'
        if (wizardCompleted && Object.values(selectedWizardOptions).length > 0) {
            // Create a title based on wizard selections
            title = Object.keys(selectedWizardOptions).map((key) => selectedWizardOptions[key].title).join(' - ')
        } else if (options.aggregation in definitions) {
            title = definitions[options.aggregation].label
        }

        const optionsRow = <div className={`row analytics-options${breadcrumbs ? ' with-breadcrumbs' : ''}`}>
            {breadcrumbs && <div style={{ display: 'flex', alignItems: 'center' }}>{breadcrumbs}</div>}

            {/* Show selected option's title on the button instead of the template title */}
            <FilterOptions
                filters={props.isDashboardTile ? filters.filter((f) => f !== AnalyticsFilter.Date && f !== AnalyticsFilter.MonthRange) : [activeDateFilter]}
                filterSubset={filters}
                queryType={QueryType.Analytics}
                ranges={ranges}>
            </FilterOptions>

            {/* {wizardCompleted && !activeTemplate && (
                    <Button
                        icon="fas fa-magic"
                        onClick={handleResetWizard}
                        className="template-active-button"
                    >
                        {Object.values(selectedWizardOptions).length > 0
                            ? Object.values(selectedWizardOptions)[Object.values(selectedWizardOptions).length - 1].title
                            : activeTemplate?.title || 'Wizard'}
                    </Button>
                )} */}
            <TableOptions onDownload={() => retrieveData(true)}>
                <IconButton
                    icon={querying ? 'fas fa-sync fa-spin' : 'fas fa-sync'}
                    onClick={handleRefresh}
                    tooltip="Refresh data (bypass cache)"
                    disabled={querying}
                />
                {getChartTypeDropdown()}
                {/* View mode toggle button */}
                <Button
                    icon={viewMode === AnalyticsViewMode.Table ? 'fas fa-chart-bar' : 'fas fa-table'}
                    onClick={toggleViewMode}
                    title={viewMode === AnalyticsViewMode.Table ? 'Switch to Chart View' : 'Switch to Table View'}
                >
                    {viewMode === AnalyticsViewMode.Table ? 'Chart View' : 'Table View'}
                </Button>
                {description && (
                    <Button
                        icon="fas fa-info-circle"
                        onClick={() => setShowInfo((x) => !x)}
                        className={showInfo ? 'active' : ''}
                        title="About this metric"
                    >
                            About
                    </Button>
                )}
                {data && data[type] && data[type].length > 0 && (
                    <Button
                        icon="fas fa-hand-sparkles"
                        onClick={handleAIAnalysis}
                        className={showAIPanel ? 'active' : ''}
                        title="Get AI-powered analysis of this data"
                        disabled={aiLoading}
                    >
                        {aiLoading ? 'Analyzing...' : 'AI Analysis'}
                        {canEditAIContext && (
                            <span
                                className={`ai-settings-cog${showAIContextEditor ? ' active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setShowAIContextEditor((x) => !x) }}
                                title="Configure AI analysis context"
                            >
                                <i className="fas fa-cog" />
                            </span>
                        )}
                    </Button>
                )}

                {/* Chart type dropdown - only show in chart view */}

                {wizardCompleted && activeTemplate && !readOnly && <Button onClick={handleResetWizard} icon="fas fa-magic">Change Analytics Type</Button>}

                {/* Only show Optional Fields dropdown if wizard is not completed */}
                {!wizardCompleted && optionalFilters.length > 0
                        && <DropdownMenu
                            text="Optional Fields"
                            right
                            value={Object.keys(optionalFiltersEnabled)}
                            items={optionalItems}
                            onChange={handleOptionalFilter}
                            closeOnSelect={false}
                        />
                }
                {/* Only show aggregation dropdown if wizard is not completed */}
                {!wizardCompleted && Object.keys(definitions).length > 1
                        && <DropdownMenu
                            text={options.aggregation in definitions ? `By ${definitions[options.aggregation].label}` : 'Select Aggregation'}
                            value={options.aggregation}
                            items={Object.keys(definitions).map((x) => ({ value: x, text: `By ${definitions[x].label}` }))}
                            onChange={handleAggregation}
                        />
                }
                {props.onAddToDashboard && dashboards.length > 0 && (
                    <DropdownMenu
                        icon="fas fa-tachometer-alt"
                        className="add-to-dashboard-button"
                        onChange={handleAddToDashboard}
                        items={dashboards.map((x) => ({ value: x.id, text: x.name, icon: 'fas fa-plus' }))}
                    />
                )}
            </TableOptions>
        </div>

        const topbarElem = <React.Fragment>
            <div className="admin-table-topbar" style={{ flexDirection: 'column' }}>
                <div className="middle center admin-title">
                    <div className="table-header">
                        {!readOnly
                            ? <DropdownMenu
                                buttonClass="no-bg"
                                asGrid={true}
                                gridSize={5}
                                icon={iconName}
                                items={Object.values(analyticsIcons).map((x) => ({ value: x, icon: x }))}
                                onChange={handleIconName}
                            />
                            : <Icon secondary noBg icon={iconName} />
                        }
                        <div className="table-title-content">
                            {!readOnly
                                ? <>
                                    <Input className="table-title-input" value={tableName} placeholder="Title" onChange={handleTableName} />
                                    <Input className="table-description-input" textarea textareaRows={1} value={description} placeholder="Description" onChange={handleDescription} />
                                </>
                                : <>
                                    <h2>{tableName}</h2>
                                    {description && <p className="table-description">{description}</p>}
                                </>
                            }
                        </div>
                    </div>
                </div>
                {(!props.isDashboardTile || (!props.topbarPortalTarget && !readOnly)) && optionsRow}
                {showInfo && description && (
                    <div className="analytics-info-panel">
                        <Icon noBg icon="fas fa-info-circle" className="analytics-info-panel-icon" />
                        <div className="analytics-info-panel-text">
                            {description.split('\n\n').map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                        </div>
                        <IconButton noBg icon="fas fa-times" className="analytics-info-panel-close" onClick={() => setShowInfo(false)} />
                    </div>
                )}
                <FilterList type={QueryType.Analytics} filters={filters} maps={maps} fields={getFields()} />
            </div>
            {props.topbarPortalTarget
                ? ReactDOM.createPortal(optionsRow, props.topbarPortalTarget)
                : null}
        </React.Fragment>

        if (!data) {
            return <div className="admin-table-wrapper fadeIn">
                {topbarElem}
                <Message info="No Analytics Data Loaded"/>
            </div>
        }

        if (data.error) {
            return <div className="data-list admin-table-wrapper filter-table fadeIn">
                {topbarElem}
                <h3>{data.error}</h3>
            </div>
        }

        const sortedRows = getSortedRows()

        // return <div className="data-list admin-table-wrapper filter-table fadeIn"></div>
        return <div className="admin-table-wrapper fadeIn">
            {topbarElem}
            {showAIPanel && (
                <div className="analytics-ai-card-container">
                    <div className="analytics-ai-card-header">
                        <div className="analytics-ai-card-title">
                            <i className="fas fa-hand-sparkles" />
                            <span>AI Analysis</span>
                            {!aiLoading && aiAnalysis?.overallScore != null && (
                                <span className={`analytics-ai-score${aiAnalysis.overallScore >= 70 ? ' good' : aiAnalysis.overallScore >= 40 ? ' moderate' : ' low'}`}>
                                    Score: {aiAnalysis.overallScore}/100
                                </span>
                            )}
                        </div>
                        <IconButton noBg icon="fas fa-times" onClick={() => { setShowAIPanel(false); setAiAnalysis(null); setAiError(null) }} />
                    </div>
                    {aiLoading && (
                        <div className="analytics-ai-loading">
                            <Spinner showAfter={0} quotes={false} invert={true} />
                            <span>Analyzing your data...</span>
                        </div>
                    )}
                    {aiError && (
                        <div className="analytics-ai-error">
                            <i className="fas fa-exclamation-triangle" />
                            <span>{aiError}</span>
                            <Button icon="fas fa-redo" onClick={handleAIAnalysis}>Retry</Button>
                        </div>
                    )}
                    {!aiLoading && !aiError && aiAnalysis && (
                        <div className="analytics-ai-body">
                            {aiAnalysis.summary && (
                                <p className="analytics-ai-summary">{aiAnalysis.summary}</p>
                            )}
                            <div className="analytics-ai-sections">
                                {aiAnalysis.highlights && aiAnalysis.highlights.length > 0 && (
                                    <div className="analytics-ai-section">
                                        <h4><i className="fas fa-star" />Highlights</h4>
                                        <div className="analytics-ai-chips">
                                            {aiAnalysis.highlights.map((h: any, i: number) => (
                                                <div key={i} className={`analytics-ai-chip sentiment-${h.sentiment || 'neutral'}`}>
                                                    <strong>{h.title}</strong>
                                                    <span>{h.detail}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {aiAnalysis.insights && aiAnalysis.insights.length > 0 && (
                                    <div className="analytics-ai-section">
                                        <h4><i className="fas fa-lightbulb" />Insights</h4>
                                        <div className="analytics-ai-chips">
                                            {aiAnalysis.insights.map((ins: any, i: number) => (
                                                <div key={i} className={`analytics-ai-chip impact-${ins.impact || 'medium'}`}>
                                                    <div className="analytics-ai-chip-head">
                                                        <strong>{ins.title}</strong>
                                                        {ins.impact && <span className={`analytics-ai-badge impact-${ins.impact}`}>{ins.impact}</span>}
                                                    </div>
                                                    <span>{ins.detail}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                                    <div className="analytics-ai-section">
                                        <h4><i className="fas fa-tasks" />Recommendations</h4>
                                        <div className="analytics-ai-recs">
                                            {aiAnalysis.recommendations.map((rec: any, i: number) => (
                                                <div key={i} className="analytics-ai-rec">
                                                    <span className="analytics-ai-rec-num">{rec.priority || i + 1}</span>
                                                    <div>
                                                        <strong>{rec.action}</strong>
                                                        <span>{rec.rationale}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {showAIContextEditor && canEditAIContext && (
                <AnalyticsAIContextEditor
                    app={app}
                    aggregation={options?.aggregation ?? ''}
                    aggregationLabel={options?.aggregation && definitions?.[options.aggregation]?.label ? definitions[options.aggregation].label : (options?.aggregation ?? '')}
                    onClose={() => setShowAIContextEditor(false)}
                />
            )}
            {/* Conditionally render table or chart view based on viewMode */}
            {viewMode === AnalyticsViewMode.Table ? (
                <div className={`admin-table scrollable show-scroll${querying ? ' querying' : ''}${definitions[options.aggregation]?.inverted ? ' inverted' : ''}`} ref={scrollRef}>
                    {definitions[options.aggregation]?.inverted && sortedRows && Array.isArray(sortedRows) && sortedRows.length > 0 ? (
                        renderInvertedTable()
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    {getFields().map(getSortHeader)}
                                </tr>
                            </thead>
                            <tbody>
                                {getData()}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div className="analytics-chart-container">
                    {sortedRows && sortedRows.length > 0 ? (
                        <AnalyticsChartView
                            readOnly={readOnly}
                            data={sortedRows}
                            definition={definitions[options.aggregation]}
                            maps={maps}
                            chartType={chartType}
                            onChartTypeChange={handleChartType}
                            loading={querying}
                            transitioning={isTransitioning}
                            marginModifier={props.marginModifier}
                            aggregation={options.aggregation}
                            onPointClick={handleChartPointClick}
                        />
                    ) : (
                        <p>No data available for chart visualization</p>
                    )}
                </div>
            )}

            {/* Only show pagination for table view */}
            {viewMode === AnalyticsViewMode.Table && (
                <div className="admin-table-topbar page-nav">
                    <IconButton className={options.page == 0 ? 'disabled' : ''} icon="fas fa-chevron-left" onClick={prevPage} />
                    <Button className='disabled'>Page {options.page + 1}</Button>
                    <IconButton className={data[type] && data[type].length < options.count ? 'disabled' : ''} icon="fas fa-chevron-right" onClick={nextPage} />
                </div>
            )}

            {/* Show benchmark legend for funnel by source and campaign views */}
            {/* CACBenchmarkLegend is now rendered inside AnalyticsStackedFunnelChart */}

            {drilldownPoint && (
                <AnalyticsDrilldownPanel
                    app={app}
                    type={type}
                    aggregation={options.aggregation}
                    filters={options.filters}
                    point={drilldownPoint}
                    onClose={() => setDrilldownPoint(null)}
                />
            )}
        </div>
    }

    const [showCancelButton, setShowCancelButton] = useState(false)

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null
        if (querying) {
            setShowCancelButton(false)
            timer = setTimeout(() => setShowCancelButton(true), 3000)
        } else {
            setShowCancelButton(false)
        }
        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [querying])

    // Cancel button is now rendered inside the analytics-loading-overlay

    // Add a handler for retrying data retrieval
    function handleRetry() {
        setCancelled(false) // Exit the cancelled state
        analyticsQuery.refetch().catch(logger.error)
    }

    function handleChartPointClick(payload: AnalyticsChartPointSelection) {
        setDrilldownPoint(payload)
    }

    // Update the rendering logic to replace the loading spinner with a message when cancelled
    const loadingIndicator = !showWizard && loaded < 2 ? (
        cancelled ? (
            <Message info="Query cancelled. Please try again">
                <Button
                    className="retry-query-btn"
                    icon="fas fa-redo"
                    onClick={handleRetry}
                >
                    Try Again
                </Button>
            </Message>
        ) : (
            <div className="analytics-loading-overlay">
                <Spinner showAfter={0} quotes={false} invert={true} />
                <span className="analytics-loading-label">Loading analytics...</span>
                {showCancelButton && (
                    <button className="analytics-loading-cancel" onClick={handleCancelQuery}>
                        <i className="fas fa-times" /> Cancel
                    </button>
                )}
            </div>
        )
    ) : null

    return (
        <React.Fragment>
            <div className={`analytics-table${readOnly ? ' read-only' : ''}${compactMode ? ' compact' : ''}${isTransitioning ? ' transitioning' : ''}${querying ? ' querying' : ''}`} ref={scrollRef}>
                {showWizard && activeTemplate && <React.Fragment>
                    {compactMode ? (<div className="column" style={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        {/* <h4 style={{ margin: 'auto' }}> Expand to configure Analytics</h4> */}
                        <Button icon="fas fa-arrow-right" onClick={() => handleTileOperation(tile?.id, 'view')}> Configure Analytics Tile</Button>
                    </div>)
                        : <AnalyticsWizard
                            template={activeTemplate}
                            onComplete={handleWizardComplete}
                            onCancel={handleWizardCancel}
                        />}
                </React.Fragment>
                }

                {!showWizard && loaded >= 2 && getAnalyticView()}
                {loadingIndicator}

                {/* Fullscreen reload overlay — shown when re-querying with existing data */}
                {querying && (
                    <div className="analytics-loading-overlay">
                        <Spinner showAfter={0} quotes={false} invert={true} />
                        <span className="analytics-loading-label">Loading analytics...</span>
                        {showCancelButton && (
                            <button className="analytics-loading-cancel" onClick={handleCancelQuery}>
                                <i className="fas fa-times" /> Cancel
                            </button>
                        )}
                    </div>
                )}
            </div>
        </React.Fragment>)
}
